import { isEqual } from "lodash";

/**
 * Helper function to generate a dictionary from an array of objects
 * @param {*} arr - array of objects
 * @param {*} key - key to use as the dictionary key
 * @param {*} value - key to use as the dictionary value
 * @returns - generated dictionary
 */
const genDictionary = (arr, key = "value", value = "display") =>
  arr.reduce((dict, obj) => {
    if (!dict[obj[key]]) dict[obj[key]] = obj[value];
    return dict;
  }, {});

/**
 * Helper function to compare two objects and return an array of changed properties
 * @param {*} before - existing data
 * @param {*} after - updated data
 * @returns - generated changes array
 */
function compareObjects(before, after) {
  return Array.from(new Set([...Object.keys(before), ...Object.keys(after)])).reduce(
    (changes, key) => {
      if (
        !Object.hasOwn(before, key) ||
        !Object.hasOwn(after, key) ||
        !isEqual(after[key], before[key])
      )
        changes.push({
          key,
          before: before[key],
          after: after[key],
        });
      return changes;
    },
    []
  );
}

/**
 * Helper function to detect a changes between two objects
 * @param {*} before - existing data
 * @param {*} after - updated data
 * @returns generated changes array, teamsDict, kpisDict
 */
function detectChanges(before, after) {
  const changes = [];

  // Generate kpi and team data directories to easily look up names
  const teamsDict = genDictionary(before?.dropDownOptions?.teamIds || []);
  const kpisDict = genDictionary(before?.dropDownOptions?.kpis || []);

  // generate default values changes
  const defaultsChanges = compareObjects(before.defaultValues, after.defaultValues);
  if (defaultsChanges.length > 0) {
    changes.push({
      type: "modified_defaults",
      kpiId: "default",
      kpiName: "Default Values",
      alertKey: "default",
      changes: defaultsChanges,
    });
  }

  // generate delivery mechanisms changes
  const deliveryChanges = compareObjects(
    { deliveryMechanisms: before.deliveryMechanisms },
    { deliveryMechanisms: after.deliveryMechanisms }
  );
  if (deliveryChanges.length > 0) {
    changes.push({
      type: "modified_defaults",
      kpiId: "default",
      kpiName: "Default Values",
      alertKey: "default",
      changes: deliveryChanges,
    });
  }

  // removed kpis changes
  Object.keys(before.alerts || {}).forEach((kpiId) => {
    if (!after.alerts?.[kpiId]) {
      changes.push({
        type: "removed_kpi",
        kpiId,
        kpiName: kpisDict[kpiId],
        details: before.alerts[kpiId],
      });
    }
  });

  Object.keys(after.alerts || {}).forEach((kpiId) => {
    const beforeKpi = before.alerts?.[kpiId];
    const afterKpi = after.alerts[kpiId];

    // New added kpis changes
    if (!beforeKpi) {
      changes.push({
        type: "added_kpi",
        kpiId,
        kpiName: kpisDict[kpiId],
        details: afterKpi,
      });
    } else {
      // Compare each nested alert object, now treated as arrays
      const beforeAlerts = Object.values(beforeKpi);
      const afterAlerts = Object.values(afterKpi);

      // Compare the alerts at the same index
      // eslint-disable-next-line no-plusplus
      for (let i = 0; i < Math.max(beforeAlerts.length, afterAlerts.length); i++) {
        const beforeAlert = beforeAlerts[i];
        const afterAlert = afterAlerts[i];

        // If either alert doesn't exist in one of the objects (missing or different length)
        if (!beforeAlert) {
          // New added alerts changes
          changes.push({
            type: "added_alert",
            kpiId,
            kpiName: kpisDict[kpiId],
            alertKey: Object.keys(afterKpi)[i],
            details: afterAlert,
          });
        } else if (!afterAlert) {
          // Removed alerts changes
          changes.push({
            type: "removed_alert",
            kpiId,
            kpiName: kpisDict[kpiId],
            alertKey: Object.keys(beforeKpi)[i],
            details: beforeAlert,
          });
        } else {
          // If both exist, compare their properties for changes
          const alertChanges = compareObjects(beforeAlert, afterAlert);
          if (alertChanges.length > 0) {
            // Modified alerts changes
            changes.push({
              type: "modified_alert",
              kpiId,
              kpiName: kpisDict[kpiId],
              alertKey: Object.keys(beforeKpi)[i],
              changes: alertChanges,
            });
          }
        }
      }
    }
  });

  return { changes, teamsDict, kpisDict };
}

// Styles for the changelog HTML
const styles = {
  changeGroup:
    "font-size: 1rem; border: 1px solid #eee; border-radius: 6px; padding: 15px; margin-bottom: 15px; background-color: #f8fafc;",
  changeHeader:
    "font-size: 1.1em; font-weight: bold; color: #2d3748; margin-bottom: 10px; padding-bottom: 5px; border-bottom: 2px solid #e2e8f0;",
  changeItem: "margin: 8px 0; padding-left: 20px; line-height: 1.5;",
  alertConfig:
    "margin-left: 20px; padding: 8px; background-color: #fff; border-left: 3px solid #cbd5e0; margin-top: 5px;",
  added: "color: #2f855a; border-left-color: #2f855a;",
  removed: "color: #c53030; border-left-color: #c53030;",
  modified: "color: #ffa500; border-left-color: #ffa500;",
  kpiName: "font-weight: 600; color: #1a202c;",
};

/**
 * Helper function to generate a string of team names
 * @param {*} teams - array of team ids
 * @param {*} teamsDict - teams dictionary
 * @returns - generated string of team names
 */
const generateTeamsChangeLog = (teams, teamsDict) => teams.map((id) => teamsDict[id]).join(", ");

/**
 * Helper function to generate a string of report on change log
 * @param {*} obj - object of report on changes
 * @returns - generated string of report on changes
 */
const generateReportOnChangeLog = (obj) =>
  Object.entries(obj)
    .map(([key, value]) => (value ? `${key.toUpperCase()} Level Insights` : null))
    .filter((convertedStr) => !!convertedStr)
    .join(", ") || "Empty";

/**
 * Helper function to format alert details
 * @param {*} details - alert
 * @param {*} teamsDict - teams dictionary
 * @returns - formatted alert details
 */
function formatAlertDetails(details, teamsDict) {
  const parts = [];
  if (details.type)
    parts.push(`<b>Threshold:</b> ${details.type} by ${Number(details?.threshold || 0) * 100}%`);
  if (details.period) parts.push(`<b>Trailing Average:</b> ${details.period} days`);
  if (details.interval) parts.push(`<b>Alert Type:</b> ${details.interval}`);
  if (details.reportOn) {
    parts.push(`<b>Insights Alert Type:</b> ${generateReportOnChangeLog(details.reportOn)}`);
  }
  if (details.teamIds)
    parts.push(`<b>Report On:</b> ${generateTeamsChangeLog(details.teamIds, teamsDict)}`);
  // eslint-disable-next-line no-prototype-builtins
  if (details.hasOwnProperty("status"))
    parts.push(`<b>Status:</b> ${details.status ? "On" : "Off"}`);
  return parts.join("<br>");
}

/**
 * Helper function to format modified alert details
 * @param {*} changes - array of changes
 * @param {*} teamsDict - teams dictionary
 * @returns - formatted modified alert details
 */
function formatModifiedAlertDetails(changes, teamsDict) {
  return changes
    .map((modification) => {
      let text = "";
      const deliveryMechanismsChanges = [];

      switch (modification.key) {
        case "type":
          text = `<b>Threshold:</b> ${modification.before} by ${
            Number(modification?.threshold?.before || 0) * 100
          }% → ${modification.after} by ${Number(modification?.threshold?.after || 0) * 100}%`;
          break;
        case "period":
          text = `<b>Trailing Average:</b> ${modification.before} days → ${modification.after} days`;
          break;
        case "interval":
          text = `<b>Alert Type:</b> ${modification.before} → ${modification.after}`;
          break;
        case "reportOn":
          text = `<b>Insights Alert Type:</b> ${generateReportOnChangeLog(
            modification.before
          )} → ${generateReportOnChangeLog(modification.after)}`;
          break;
        case "teamIds":
          text = `<b>Report On:</b> ${generateTeamsChangeLog(
            modification.before,
            teamsDict
          )} → ${generateTeamsChangeLog(modification.after, teamsDict)}`;
          break;
        case "status":
          text = `<b>Status:</b> ${modification.before ? "On" : "Off"} → ${
            modification.after ? "On" : "Off"
          }`;
          break;
        case "deliveryMechanisms":
          if (modification.before.email !== modification.after.email) {
            deliveryMechanismsChanges.push(
              `<b>Email:</b> ${modification.before.email ? "On" : "Off"} → ${
                modification.after.email ? "On" : "Off"
              }`
            );
          }
          if (modification.before.inApp !== modification.after.inApp) {
            deliveryMechanismsChanges.push(
              `<b>In App:</b> ${modification.before.inApp ? "On" : "Off"} → ${
                modification.after.inApp ? "On" : "Off"
              }`
            );
          }
          text = deliveryMechanismsChanges.join("<br>");
          break;

        default:
          break;
      }
      return text;
    })
    .join("<br>");
}

/**
 * Helper function to generate a changelog HTML
 * @param {*} before - existing data
 * @param {*} after - updated data
 * @returns - generated changelog HTML
 */
export const generateChangelogHTML = async (before, after) => {
  const { changes, teamsDict, kpisDict } = detectChanges(before, after);

  let html = "<h4 style='margin-bottom:10px;'>Configuration Changes Summary :</h4>";

  // Group changes by KPI
  const groupedChanges = changes.reduce((acc, change) => {
    if (!acc[change.kpiId]) {
      acc[change.kpiId] = [];
    }
    acc[change.kpiId].push(change);
    return acc;
  }, {});

  // Loop through each grouped KPI and generate the HTML
  Object.entries(groupedChanges).forEach(([kpiId, kpiChanges]) => {
    html += `
        <div style="${styles.changeGroup}">
            <div style="${styles.changeHeader}">
                ${kpiId === "default" ? "Default Values" : kpisDict[kpiId]}
            </div>
    `;

    kpiChanges.forEach((change) => {
      switch (change.type) {
        case "removed_kpi":
          html += `
                    <div style="${styles.changeItem}">
                        <span style="${styles.removed}"><b>✗ KPI configuration removed</b></span>
                        <div style="${styles.alertConfig} ${styles.removed}">
                            ${Object.keys(change.details)
                              .map(
                                (alertKey) =>
                                  `${formatAlertDetails(change.details[alertKey], teamsDict)}`
                              )
                              .join("<br><br>")}
                        </div>
                    </div>`;
          break;

        case "added_kpi":
          html += `
                    <div style="${styles.changeItem}">
                        <span style="${styles.added}"><b>✓ New KPI configuration added</b></span>
                        <div style="${styles.alertConfig} ${styles.added}">
                            ${Object.keys(change.details)
                              .map(
                                (alertKey) =>
                                  `${formatAlertDetails(change.details[alertKey], teamsDict)}`
                              )
                              .join("<br><br>")}
                        </div>
                    </div>`;
          break;

        case "added_alert":
          html += `
                    <div style="${styles.changeItem}">
                        <span style="${styles.added}"><b>✓ New alert configuration added</b></span>
                        <div style="${styles.alertConfig} ${styles.added}">
                            ${formatAlertDetails(change.details, teamsDict)}
                        </div>
                    </div>`;
          break;

        case "removed_alert":
          html += `
                    <div style="${styles.changeItem}">
                        <span style="${styles.removed}"><b>✗ Alert configuration removed</b></span>
                        <div style="${styles.alertConfig} ${styles.removed}">
                            ${formatAlertDetails(change.details, teamsDict)}
                        </div>
                    </div>`;
          break;

        case "modified_alert":
          html += `
                    <div style="${styles.changeItem}">
                        <span style="${
                          styles.modified
                        }"><b>✎ Alert configuration modified</b></span>
                        <div style="${styles.alertConfig} ${styles.modified}">
                            <b>Modified Details:</b><br>
                            ${formatModifiedAlertDetails(change.changes, teamsDict)}
                        </div>
                    </div>`;
          break;

        case "modified_defaults":
          html += `
                    <div style="${styles.changeItem}">
                        <div style="${styles.alertConfig} ${styles.modified}">
                            <b>Modified Details:</b><br>
                            ${formatModifiedAlertDetails(change.changes, teamsDict)}
                        </div>
                    </div>`;
          break;
        default:
          break;
      }
    });

    html += "</div>";
  });

  return html;
};
